/**
 * @lodex-pro/web-shared/auth/email-totp-flow — multi-step Amplify v6 signIn React component.
 *
 * Phase 4 / Plan 04-09 — implements Sequence Diagram E (RESEARCH lines 1175-1217)
 * verbatim. The component walks the user through the Amplify v6 signIn state machine:
 *
 *   1. `idle` → user enters email + password → on submit, call `signIn`.
 *   2. switch on `nextStep.signInStep`:
 *        - `CONFIRM_SIGN_IN_WITH_TOTP_CODE` → render 6-digit TOTP input.
 *        - `CONFIRM_SIGN_IN_WITH_SMS_CODE`  → render 6-digit SMS OTP input (fallback).
 *        - `CONTINUE_SIGN_IN_WITH_TOTP_SETUP` → fire `onTotpSetupRequired` prop so the
 *          consumer app routes to the `TotpEnrollment` component.
 *        - `CONTINUE_SIGN_IN_WITH_MFA_SELECTION` → render an MFA chooser (TOTP / SMS).
 *        - `CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED` → fire optional
 *          `onNewPasswordRequired` callback (DEC-152) and park in terminal
 *          `awaiting-new-password-handoff` state (renders nothing). If callback
 *          is NOT provided, falls through to the default-case unsupported-step error
 *          (backwards-compat for web-admin / web-dispatch / web-billing).
 *        - `DONE` (`isSignedIn: true`) → fire `onSignedIn` prop.
 *   3. On `confirmSignIn` success → fire `onSignedIn`.
 *
 * Errors caught from Amplify are mapped via `mapAmplifyError`. The user sees the
 * mapped userMessage (NEVER the raw Cognito error name — T-04-09-05 mitigation).
 *
 * UI: semantic HTML (`<form>`, `<input>`, `<button>`) with NO design system. The
 * consumer app's CSS skins the elements via class selectors — plan 04-10 ships the
 * Tailwind dark-theme styling per CLAUDE.md ("Tailwind dark from day one"). Body
 * text ≥ 16px and touch targets reasonably sized are enforced by the consumer's
 * stylesheet, not by this component.
 *
 * Recovery codes: NOT shipped here (Discretion #4 deferred per W7+W9 — plan 04-02
 * also dropped the `user_recovery_codes` schema). A banner instructs lost-device
 * users to contact admin. Phase 5 follow-on ships BOTH the schema and the UI together.
 *
 * Spec: master spec §7 (auth flows); CONTEXT D-05 (Amplify v6 web);
 *       RESEARCH § Sequence Diagram E + § Code Example 6 (lines 951-989) +
 *       § Pitfall 9 (lines 561-572 — nextStep branching).
 *       DEC-152: additive `onNewPasswordRequired` callback for FORCE_CHANGE_PASSWORD flow.
 */

import { useState, type FC } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { signIn, confirmSignIn } from 'aws-amplify/auth';

import { mapAmplifyError } from './error-mapper.js';

/**
 * Shown when Cognito returns a sign-in step this build cannot complete.
 *
 * Deliberately says nothing about WHICH step. The raw `signInStep` is a Cognito
 * implementation identifier (`CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION` and the
 * like): it is not actionable for the person signing in, and putting it on screen
 * is the same leak T-04-09-05 closed for exception names in error-mapper.ts. It is
 * logged for developers instead — see reportUnsupportedStep.
 *
 * "Contact your administrator" rather than "try again", because retrying cannot
 * help: the step will be returned again until the app implements it.
 */
const UNSUPPORTED_STEP_MESSAGE =
  'Sign-in could not be completed. Contact your administrator for help.';

/**
 * Record an unhandled Cognito sign-in step for developers.
 *
 * web-shared has no logger — this is the package's only console call, and it is
 * here on purpose: the identifier is the single piece of information that tells a
 * developer which step needs implementing, and discarding it to keep the package
 * console-free would make an unsupported-step report unactionable. It never
 * reaches the UI.
 */
function reportUnsupportedStep(nextStepKind: string): void {
  // Diagnostic only; never rendered (see above). No eslint-disable needed — the repo
  // does not enable no-console, and an unused directive is itself a lint warning.
  console.error(`[web-shared/auth] unsupported Cognito sign-in step: ${nextStepKind}`);
}
import { PasswordField } from './password-field.js';
import { SupportContactLine } from '../support/support-contact.js';

/**
 * Internal state machine. The discriminator drives which form/UI to render.
 *
 *   - `idle`            → email+password form
 *   - `awaiting-totp`   → 6-digit TOTP code form (CONFIRM_SIGN_IN_WITH_TOTP_CODE)
 *   - `awaiting-sms-otp`→ 6-digit SMS code form (CONFIRM_SIGN_IN_WITH_SMS_CODE)
 *   - `mfa-selection`   → choose TOTP vs SMS at challenge time
 *                         (CONTINUE_SIGN_IN_WITH_MFA_SELECTION)
 *   - `signed-in`       → terminal (onSignedIn already fired; we render nothing)
 *   - `awaiting-new-password-handoff` → terminal (DEC-152: onNewPasswordRequired
 *                         callback already fired; consumer swaps in ForcedPasswordChange)
 *
 * The `CONTINUE_SIGN_IN_WITH_TOTP_SETUP` branch transitions out of the component
 * entirely via the `onTotpSetupRequired` prop — the consumer routes to TotpEnrollment.
 */
type FlowState =
  | { kind: 'idle' }
  | { kind: 'awaiting-totp' }
  | { kind: 'awaiting-sms-otp' }
  | { kind: 'mfa-selection'; options: ReadonlyArray<'TOTP' | 'SMS'> }
  | { kind: 'signed-in' }
  | { kind: 'awaiting-new-password-handoff' };

/**
 * Zod schema for the email+password form. Email must match the standard RFC 5322
 * pattern (Zod's built-in). Password must be at least 12 chars per the Phase 4
 * Cognito pool policy (plan 04-01) — Cognito will reject shorter passwords with
 * `InvalidPasswordException` server-side, but client validation gives faster
 * feedback for the common "I typed too few characters" case.
 */
const credentialsSchema = z.object({
  email: z.string().email('Enter a valid email address.'),
  password: z.string().min(12, 'Password must be at least 12 characters.'),
});
type CredentialsForm = z.infer<typeof credentialsSchema>;

/** 6-digit MFA / SMS OTP. Pattern `^\d{6}$` enforces exactly six digits. */
const codeSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code.'),
});
type CodeForm = z.infer<typeof codeSchema>;

/**
 * Props for the EmailTotpFlow component.
 *
 *   - `onSignedIn`         fired when Amplify's signIn / confirmSignIn returns
 *                          `isSignedIn: true`. The consumer routes to the dashboard.
 *   - `onTotpSetupRequired` fired when Amplify returns
 *                          `CONTINUE_SIGN_IN_WITH_TOTP_SETUP`. The consumer routes
 *                          to the `TotpEnrollment` component.
 *   - `onNewPasswordRequired` (DEC-152) — optional. Fired when Amplify returns
 *                          `CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED` (Cognito
 *                          FORCE_CHANGE_PASSWORD state after admin-provisioned account).
 *                          Context includes `{ username }` (the email submitted).
 *                          After firing, the component parks in a terminal
 *                          `awaiting-new-password-handoff` state and renders nothing —
 *                          the consumer is expected to swap in `ForcedPasswordChange`.
 *                          If NOT provided, the component falls through to the existing
 *                          default-case unsupported-step error (backwards-compat for
 *                          web-admin / web-dispatch / web-billing per DEC-152 Grey Area 4).
 */
export interface EmailTotpFlowProps {
  readonly onSignedIn: () => void;
  readonly onTotpSetupRequired: () => void;
  readonly onNewPasswordRequired?: (context: { username: string }) => void;
  /**
   * When given, a link-style "Forgot your password?" sits right under the Sign in button on
   * the credentials step (SignInScreen passes it; 2026-10-21).
   */
  readonly onForgotPassword?: () => void;
}

/**
 * The component. Pure UI — no global state, no router knowledge. Consumer apps
 * embed this inside their sign-in page and route on the callback props.
 */
export const EmailTotpFlow: FC<EmailTotpFlowProps> = ({
  onSignedIn,
  onTotpSetupRequired,
  onNewPasswordRequired,
  onForgotPassword,
}) => {
  const [state, setState] = useState<FlowState>({ kind: 'idle' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const credentialsForm = useForm<CredentialsForm>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
  });
  const codeForm = useForm<CodeForm>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  });

  const handleSignInResult = (
    isSignedIn: boolean,
    nextStepKind: string,
    mfaOptions?: ReadonlyArray<'TOTP' | 'SMS'>,
    contextForCallback?: { username: string },
  ): void => {
    if (isSignedIn) {
      setState({ kind: 'signed-in' });
      onSignedIn();
      return;
    }
    switch (nextStepKind) {
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        setState({ kind: 'awaiting-totp' });
        return;
      case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
        setState({ kind: 'awaiting-sms-otp' });
        return;
      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
        // The consumer routes to the TotpEnrollment screen.
        onTotpSetupRequired();
        return;
      case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
        setState({
          kind: 'mfa-selection',
          options: mfaOptions ?? (['TOTP', 'SMS'] as const),
        });
        return;
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        // DEC-152: Cognito FORCE_CHANGE_PASSWORD state after admin-provisioned account.
        // If the consumer provided the callback, fire it and park in a terminal state.
        // If NOT provided, fall through to the default-case error (backwards-compat).
        if (onNewPasswordRequired !== undefined) {
          setState({ kind: 'awaiting-new-password-handoff' });
          onNewPasswordRequired({ username: contextForCallback?.username ?? '' });
          return;
        }
        // Callback not provided — surface as unsupported-step error (backwards-compat
        // for web-admin / web-dispatch / web-billing per DEC-152 Grey Area 4).
        reportUnsupportedStep(nextStepKind);
        setErrorMessage(UNSUPPORTED_STEP_MESSAGE);
        return;
      default:
        // Unhandled next step — surface the generic error. Future Cognito
        // additions (e.g. passkeys) land here until plan-level support is added,
        // and the identifier goes to the console rather than to the user.
        reportUnsupportedStep(nextStepKind);
        setErrorMessage(UNSUPPORTED_STEP_MESSAGE);
        return;
    }
  };

  const onCredentialsSubmit: SubmitHandler<CredentialsForm> = async (values) => {
    setErrorMessage(null);
    try {
      const result = await signIn({ username: values.email, password: values.password });
      const stepKind = result.nextStep.signInStep;
      const mfaOptions =
        stepKind === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION'
          ? (result.nextStep.allowedMFATypes ?? []).filter(
              (t): t is 'TOTP' | 'SMS' => t === 'TOTP' || t === 'SMS',
            )
          : undefined;
      // DEC-152: thread the email as contextForCallback so onNewPasswordRequired
      // receives the username the user submitted.
      handleSignInResult(result.isSignedIn, stepKind, mfaOptions, { username: values.email });
    } catch (err) {
      setErrorMessage(mapAmplifyError(err).userMessage);
    }
  };

  const onCodeSubmit: SubmitHandler<CodeForm> = async (values) => {
    setErrorMessage(null);
    try {
      const result = await confirmSignIn({ challengeResponse: values.code });
      const stepKind = result.nextStep.signInStep;
      handleSignInResult(result.isSignedIn, stepKind);
    } catch (err) {
      setErrorMessage(mapAmplifyError(err).userMessage);
    }
  };

  const selectMfa = async (choice: 'TOTP' | 'SMS'): Promise<void> => {
    setErrorMessage(null);
    try {
      // The MFA-selection step is confirmed via the same `confirmSignIn` call,
      // passing the chosen factor as the `challengeResponse` literal Amplify docs.
      const result = await confirmSignIn({ challengeResponse: choice });
      const stepKind = result.nextStep.signInStep;
      handleSignInResult(result.isSignedIn, stepKind);
    } catch (err) {
      setErrorMessage(mapAmplifyError(err).userMessage);
    }
  };

  if (state.kind === 'signed-in') {
    return null;
  }

  // DEC-152: terminal handoff state after onNewPasswordRequired callback fired.
  // The consumer is expected to have swapped in ForcedPasswordChange. Render nothing.
  if (state.kind === 'awaiting-new-password-handoff') {
    return null;
  }

  if (state.kind === 'idle') {
    return (
      <form
        onSubmit={(e) => {
          void credentialsForm.handleSubmit(onCredentialsSubmit)(e);
        }}
        aria-label="Sign in"
      >
        <label htmlFor="email-totp-flow-email">Email</label>
        <input
          id="email-totp-flow-email"
          type="email"
          autoComplete="email"
          {...credentialsForm.register('email')}
        />
        {credentialsForm.formState.errors.email !== undefined && (
          <p role="alert">{credentialsForm.formState.errors.email.message}</p>
        )}

        {/* Quick 260827-pwd — PasswordField adds the show/hide toggle. The id is
            unchanged: it is the selector every browser-driven check uses. */}
        <PasswordField
          label="Password"
          id="email-totp-flow-password"
          autoComplete="current-password"
          error={credentialsForm.formState.errors.password?.message}
          {...credentialsForm.register('password')}
        />

        <button type="submit" disabled={credentialsForm.formState.isSubmitting}>
          Sign in
        </button>
        {onForgotPassword !== undefined && (
          <button type="button" className="auth-link" onClick={onForgotPassword}>
            Forgot your password?
          </button>
        )}

        {errorMessage !== null && <p role="alert">{errorMessage}</p>}
        {/* OPEN-109: the platform setting, not a hard-coded (and unregistered) domain. */}
        <SupportContactLine />
      </form>
    );
  }

  if (state.kind === 'mfa-selection') {
    return (
      <div aria-label="Choose verification method">
        <p>Choose how to verify your identity:</p>
        {state.options.includes('TOTP') && (
          <button type="button" onClick={() => void selectMfa('TOTP')}>
            Use authenticator app (TOTP)
          </button>
        )}
        {state.options.includes('SMS') && (
          <button type="button" onClick={() => void selectMfa('SMS')}>
            Send a code by SMS
          </button>
        )}
        {errorMessage !== null && <p role="alert">{errorMessage}</p>}
      </div>
    );
  }

  // awaiting-totp or awaiting-sms-otp — both share the same 6-digit input UI.
  const label = state.kind === 'awaiting-totp' ? 'Authenticator code' : 'SMS code';
  return (
    <form
      onSubmit={(e) => {
        void codeForm.handleSubmit(onCodeSubmit)(e);
      }}
      aria-label={label}
    >
      <label htmlFor="email-totp-flow-code">{label}</label>
      <input
        className="keep-case"
        id="email-totp-flow-code"
        type="text"
        inputMode="numeric"
        pattern="\d{6}"
        maxLength={6}
        autoComplete="one-time-code"
        {...codeForm.register('code')}
      />
      {codeForm.formState.errors.code !== undefined && (
        <p role="alert">{codeForm.formState.errors.code.message}</p>
      )}

      <button type="submit" disabled={codeForm.formState.isSubmitting}>
        Verify
      </button>

      {errorMessage !== null && <p role="alert">{errorMessage}</p>}
    </form>
  );
};
